/**
 * Shared classification for Next.js client error boundaries.
 *
 * Rolling deploys, stale chunk caches, server-action ID drift, Turbopack
 * "module factory" mismatches, and extension-driven DOM races often recover
 * with a single full reload. Reporting these to Sentry creates noise; skipping
 * Amplitude on the same path keeps funnels honest.
 */

export function isRecoverableClientBoundaryError(error: unknown): boolean {
  if (!error) return false;

  let message = "";
  let name = "";
  let digest: string | undefined;

  if (typeof error === "string") {
    message = error;
  } else if (typeof error === "object") {
    const e = error as { message?: unknown; name?: unknown; digest?: unknown };
    if (typeof e.message === "string") message = e.message;
    if (typeof e.name === "string") name = e.name;
    if (typeof e.digest === "string") digest = e.digest;
  } else {
    return false;
  }

  const isChunkError =
    message.includes("Loading chunk") ||
    message.includes("ChunkLoadError") ||
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("Failed to load chunk") ||
    // Turbopack variant (Next.js 16): "Failed to load chunk /_next/static/chunks/…"
    /\/_next\/static\/chunks\//.test(message);

  const isStaleServerAction =
    /Failed to find Server Action|Could not find Server Action|Server Action.*not found|UnrecognizedActionError|older or newer deployment|module factory is not/i.test(
      message,
    ) || digest === "NEXT_NOT_FOUND";

  const isDomManipulationError =
    /insertBefore.*not a child|removeChild.*not a child/i.test(message) &&
    name === "NotFoundError";

  return isChunkError || isStaleServerAction || isDomManipulationError;
}

export function deploymentMismatchReloadStorageKey(pathname: string): string {
  return `deployment-reload-${pathname}`;
}

// Cooldown between reload attempts for the same pathname. A rolling GKE
// deployment typically finishes in 30–120s. If the first reload still lands
// on a transition-window pod and 404s again, we want the user's next
// interaction (or a script-tag listener) to get another automated retry
// instead of being permanently stuck behind a one-shot sessionStorage flag.
export const DEPLOYMENT_RELOAD_COOLDOWN_MS = 60_000;
// Cap total auto-reloads per session per path to avoid pathological loops
// (e.g. chunk genuinely missing on origin, not a transition issue).
export const DEPLOYMENT_RELOAD_MAX_ATTEMPTS = 3;

type ReloadState = { attempts: number; lastAt: number };

function readReloadState(pathname: string): ReloadState {
  try {
    const raw = sessionStorage.getItem(
      deploymentMismatchReloadStorageKey(pathname),
    );
    if (!raw) return { attempts: 0, lastAt: 0 };
    // Back-compat with the old "1" flag format.
    if (raw === "1") return { attempts: 1, lastAt: 0 };
    const parsed = JSON.parse(raw) as Partial<ReloadState>;
    return {
      attempts: Number(parsed.attempts) || 0,
      lastAt: Number(parsed.lastAt) || 0,
    };
  } catch {
    return { attempts: 0, lastAt: 0 };
  }
}

/** True if a deployment-mismatch reload is allowed now for this pathname. */
export function shouldAttemptDeploymentReload(pathname: string): boolean {
  const { attempts, lastAt } = readReloadState(pathname);
  if (attempts >= DEPLOYMENT_RELOAD_MAX_ATTEMPTS) return false;
  if (!lastAt) return true;
  return Date.now() - lastAt >= DEPLOYMENT_RELOAD_COOLDOWN_MS;
}

/** Record a reload attempt before calling window.location.reload(). */
export function recordDeploymentReloadAttempt(pathname: string): void {
  try {
    const prev = readReloadState(pathname);
    const next: ReloadState = {
      attempts: prev.attempts + 1,
      lastAt: Date.now(),
    };
    sessionStorage.setItem(
      deploymentMismatchReloadStorageKey(pathname),
      JSON.stringify(next),
    );
  } catch {
    // sessionStorage blocked (Brave/private mode) — best effort.
  }
}
